import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Landmark, AlertTriangle, X, ArrowRight } from "lucide-react";

import { useTranslation } from "react-i18next";
/**
 * ManagerBankDepositAlert — persistent banner on the Manager home page.
 * - Shows red banner + shortcut IF today has cash_in_hand > 0 AND no deposit yet AND today is NOT a bank holiday
 * - Shows amber "bank holiday" info chip on holidays
 * - Manager can dismiss for the day (localStorage) — but it'll reappear tomorrow
 *   (requirement: "everywhere for the Manager until they record/dismiss it")
 *
 * Backend nag ping at 16:00 IST is handled by the APScheduler nag job
 * (services/nag_bank_deposit.py) and Telegram / WhatsApp via the alert matrix.
 */
export default function ManagerBankDepositAlert() {
  const { t } = useTranslation();
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get("/manager/bank-deposits/today-status")
      .then(({ data }) => { if (alive) setStatus(data); })
      .catch(() => { /* fail silent — don't block the home page */ });

    // Per-day dismiss key so tomorrow's nag reappears
    const key = "bank-deposit-nag-dismissed-" + new Date().toISOString().slice(0, 10);
    setDismissed(localStorage.getItem(key) === "1");
    return () => { alive = false; };
  }, []);

  if (!status) return null;

  // Bank holiday — small info chip only
  if (status.is_bank_holiday) {
    return (
      <div
        className="flex items-center gap-2 rounded-lg border border-border bg-muted/50 px-3 py-2 text-xs"
        data-testid="bank-deposit-holiday-chip"
      >
        <Landmark className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="font-semibold">Bank holiday today ({status.holiday_reason})</span>
        <span className="text-muted-foreground">— no deposit reminder</span>
      </div>
    );
  }

  // Already deposited today — small green chip, non-blocking
  if (status.deposit_recorded) {
    return (
      <div
        className="flex items-center gap-2 rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-2 text-xs dark:border-emerald-900/60 dark:bg-emerald-950/40"
        data-testid="bank-deposit-recorded-chip"
      >
        <Landmark className="h-3.5 w-3.5 text-emerald-700 dark:text-emerald-400" />
        <span className="font-semibold text-emerald-900 dark:text-emerald-200">
          Today's deposit: ₹{Number(status.deposit_amount_today).toLocaleString("en-IN")}
        </span>
        <Link to="/manager/bank-deposits" className="ml-auto inline-flex items-center gap-1 text-primary hover:underline" data-testid="bank-deposit-link-recorded">
          Manage <ArrowRight className="h-3 w-3" />
        </Link>
      </div>
    );
  }

  // No deposit yet + cash on hand → nag
  if (status.cash_in_hand <= 0) return null;   // nothing to deposit
  if (dismissed) {
    // Compact reminder even after dismiss
    return (
      <div
        className="flex items-center gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2 text-xs"
        data-testid="bank-deposit-dismissed-chip"
      >
        <Landmark className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="text-muted-foreground">
          ₹{Number(status.cash_in_hand).toLocaleString("en-IN")} cash-in-hand · deposit not recorded today
        </span>
        <Link to="/manager/bank-deposits" className="ml-auto inline-flex items-center gap-1 text-primary hover:underline" data-testid="bank-deposit-link-dismissed">
          Record now <ArrowRight className="h-3 w-3" />
        </Link>
      </div>
    );
  }

  return (
    <div
      className="flex flex-wrap items-center gap-3 rounded-2xl border-2 border-red-400 bg-gradient-to-br from-red-500/10 via-background to-red-500/5 p-4 dark:border-red-900/60"
      data-testid="bank-deposit-nag-banner"
    >
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-500 text-white">
        <AlertTriangle className="h-5 w-5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-black uppercase tracking-widest text-red-800 dark:text-red-300">
          Bank deposit not recorded today
        </div>
        <div className="mt-0.5 text-xs text-foreground">
          You are holding <b>₹{Number(status.cash_in_hand).toLocaleString("en-IN")}</b> cash-in-hand.
          Deposit and record it before end of day to keep the cashbook clean.
        </div>
      </div>
      <div className="ml-auto flex items-center gap-2">
        <Link to="/manager/bank-deposits" data-testid="bank-deposit-record-cta">
          <button
            className="inline-flex items-center gap-1 rounded-lg bg-red-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-red-700"
          >
            <Landmark className="h-3.5 w-3.5" /> Record deposit
          </button>
        </Link>
        <button
          onClick={() => {
            const key = "bank-deposit-nag-dismissed-" + new Date().toISOString().slice(0, 10);
            localStorage.setItem(key, "1");
            setDismissed(true);
          }}
          className="rounded-md p-1 text-muted-foreground hover:bg-muted"
          title={t("components.managerbankdepositalert.dismiss_for_today")}
          data-testid="bank-deposit-nag-dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
